/**
 * WithdrawNowDialog — Pro Mode one-click withdrawal
 *
 * Combines: key configuration + recipient address + DKG key request + relay withdrawal
 * in a single dialog. User configures what they want, clicks "Withdraw", and everything
 * happens automatically: sign → request keys → reconstruct → relay withdraw.
 */

import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
} from '@mui/material';
import { Add, Remove, CheckCircle, Launch } from '@mui/icons-material';
import { getChainLogoUrl } from './ChainLogos';
import { getChainType, buildExplorerTxUrl } from '../utils/chainDetect';

const WithdrawNowDialog = ({
  open,
  onClose,
  deposit,
  dkgNodeStatuses,
  dkgV2Ready,
  dkgDenominations,
  supportedChains,
  validTargets = [],
  mappingsLoading,
  // Execution
  onExecute,
  // State from parent
  isDark,
  isMobile,
}) => {
  const [requests, setRequests] = useState([{ denomination: '', chain: '', token: '', recipientAddress: '' }]);
  const [phase, setPhase] = useState('config'); // config | signing | requesting | withdrawing | done
  const [progress, setProgress] = useState(null);
  const [withdrawStatuses, setWithdrawStatuses] = useState([]); // per-key status
  const [error, setError] = useState(null);

  // Derive filtered chains and per-chain tokens from validTargets
  const { filteredChains, tokensByChain } = useMemo(() => {
    if (!validTargets.length) {
      return { filteredChains: supportedChains, tokensByChain: {} };
    }
    const validChainNames = new Set(validTargets.map(t => t.chain));
    const filtered = supportedChains.filter(c => validChainNames.has(c.chain_name));
    const tokenMap = {};
    for (const t of validTargets) {
      if (!tokenMap[t.chain]) tokenMap[t.chain] = new Set();
      tokenMap[t.chain].add(t.token);
    }
    const result = {};
    for (const [chain, tokens] of Object.entries(tokenMap)) {
      result[chain] = [...tokens];
    }
    return { filteredChains: filtered, tokensByChain: result };
  }, [validTargets, supportedChains]);

  // Initialize first request chain/token when dialog opens
  React.useEffect(() => {
    if (open && deposit) {
      const token = deposit.metadata?.token || 'ETH';
      const isSolana = getChainType(deposit.userAddress) === 'solana';
      const defaultChain = isSolana ? 'solana-devnet' : 'sepolia';
      setRequests([{ denomination: '', chain: defaultChain, token, recipientAddress: '' }]);
      setPhase('config');
      setProgress(null);
      setWithdrawStatuses([]);
      setError(null);
    }
  }, [open, deposit]);

  const updateRequest = (index, field, value) => {
    setRequests(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'chain') {
        updated[index].denomination = '';
        const selectedChain = supportedChains.find(c => c.chain_name === value);
        if (selectedChain) {
          const chainType = selectedChain.chain_type || 'evm';
          updated[index].token = chainType === 'solana' ? 'SOL' : (selectedChain.native_currency || 'ETH');
        }
        updated[index].recipientAddress = '';
      }
      if (field === 'token') {
        updated[index].denomination = '';
      }
      return updated;
    });
  };

  const addRequest = () => {
    const last = requests[requests.length - 1];
    setRequests(prev => [...prev, { denomination: '', chain: last?.chain || '', token: last?.token || '', recipientAddress: '' }]);
  };

  const removeRequest = (index) => {
    if (requests.length > 1) {
      setRequests(prev => prev.filter((_, i) => i !== index));
    }
  };

  const totalRequested = requests.reduce((sum, r) => sum + (parseFloat(r.denomination) || 0), 0);
  const exceedsDeposit = deposit && totalRequested > deposit.amount;
  const validRequests = requests.filter(r => r.denomination && parseFloat(r.denomination) > 0 && r.chain && r.recipientAddress?.trim());
  const canExecute = dkgV2Ready && validRequests.length > 0 && !exceedsDeposit && validRequests.length === requests.length;

  const handleExecute = async () => {
    setPhase('signing');
    setError(null);
    try {
      await onExecute({
        deposit,
        requests: validRequests,
        onProgress: (p) => {
          setProgress(p);
          if (p.step === 'signing') setPhase('signing');
          else if (p.step === 'requesting_keys' || p.step === 'collecting') setPhase('requesting');
        },
        onKeysReady: () => setPhase('withdrawing'),
        onWithdrawStatus: (statuses) => setWithdrawStatuses(statuses),
        onDone: () => setPhase('done'),
        onError: (err) => {
          setError(err);
          setPhase('config');
        },
      });
    } catch (err) {
      setError(err.message);
      setPhase('config');
    }
  };

  const subtleText = isDark ? '#a3a3a3' : '#525252';
  const borderColor = isDark ? '#262626' : '#e5e5e5';

  const getExplorerUrl = (txHash, chainName) => buildExplorerTxUrl(chainName, txHash, supportedChains);

  const isProcessing = phase !== 'config' && phase !== 'done';

  return (
    <Dialog open={open} onClose={isProcessing ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ bgcolor: 'rgba(34, 197, 94, 0.1)', borderBottom: '1px solid rgba(34, 197, 94, 0.2)' }}>
        {phase === 'done' ? 'Unlock Complete' : 'Unlock'}
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {/* Deposit info */}
        <Box sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)', p: 2, borderRadius: 1, mb: 3 }}>
          <Typography variant="body2">
            <strong>Deposit:</strong> {deposit?.amount} {deposit?.metadata?.token || 'ETH'}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: subtleText }}>
            {deposit?.txHash?.slice(0, 16)}...{deposit?.txHash?.slice(-8)}
          </Typography>
          {deposit?.remainingDeposit !== undefined && deposit?.remainingDeposit < deposit?.amount && (
            <Typography variant="caption" sx={{ display: 'block', color: '#f59e0b', mt: 0.5 }}>
              Remaining: {deposit.remainingDeposit} {deposit?.metadata?.token || 'ETH'}
            </Typography>
          )}
        </Box>

        {/* Config phase — key rows with recipient */}
        {phase === 'config' && (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {requests.map((req, index) => {
              const chainDenoms = dkgDenominations[req.chain] || [];
              const filteredDenoms = chainDenoms.filter(d => d.tokenSymbol === req.token);
              const seen = new Set();
              const uniqueDenoms = filteredDenoms.filter(d => {
                const k = `${d.denomination}-${d.tokenSymbol}`;
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
              });
              const chainInfo = supportedChains.find(c => c.chain_name === req.chain);
              const isSolanaChain = chainInfo?.chain_type === 'solana' || req.chain?.includes('solana');

              return (
                <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid', borderColor, borderRadius: 1, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)' }}>
                  {/* Row 1: Chain + Token + Amount */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                    <FormControl sx={{ flex: 1, minWidth: 130 }} size="small">
                      <InputLabel>Chain</InputLabel>
                      <Select value={req.chain} onChange={(e) => updateRequest(index, 'chain', e.target.value)} label="Chain">
                        {filteredChains.map(chain => (
                          <MenuItem key={chain.chain_name} value={chain.chain_name}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getChainLogoUrl(chain.chain_name) && (
                                <Box component="img" src={getChainLogoUrl(chain.chain_name)} alt={chain.name} sx={{ width: 16, height: 16, borderRadius: '50%' }} />
                              )}
                              {chain.name}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 90 }} size="small">
                      <InputLabel>Token</InputLabel>
                      <Select value={req.token} onChange={(e) => updateRequest(index, 'token', e.target.value)} label="Token">
                        {(() => {
                          const validTokens = tokensByChain[req.chain];
                          if (validTokens?.length) return validTokens.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>);
                          const sc = supportedChains.find(c => c.chain_name === req.chain);
                          if (!sc?.supported_tokens?.length) return <MenuItem value="" disabled>-</MenuItem>;
                          return sc.supported_tokens.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>);
                        })()}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 100 }} size="small">
                      <InputLabel>Amount</InputLabel>
                      <Select value={req.denomination} onChange={(e) => updateRequest(index, 'denomination', e.target.value)} label="Amount" disabled={!req.chain}>
                        {uniqueDenoms.length === 0
                          ? <MenuItem value="" disabled>No amounts</MenuItem>
                          : uniqueDenoms.map(d => <MenuItem key={`${d.denomination}-${d.tokenSymbol}`} value={d.denomination.toString()}>{d.denomination}</MenuItem>)
                        }
                      </Select>
                    </FormControl>
                    {requests.length > 1 && (
                      <IconButton onClick={() => removeRequest(index)} color="error" size="small" sx={{ alignSelf: 'center' }}>
                        <Remove />
                      </IconButton>
                    )}
                  </Box>
                  {/* Row 2: Recipient address */}
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={isSolanaChain ? 'Solana recipient address' : '0x... recipient address'}
                    value={req.recipientAddress}
                    onChange={(e) => updateRequest(index, 'recipientAddress', e.target.value)}
                    disabled={!req.denomination}
                    InputProps={{ sx: { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8rem' } }}
                  />
                </Box>
              );
            })}

            {/* Add row + total */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Button onClick={addRequest} startIcon={<Add />} size="small" sx={{ color: subtleText }}>
                Add Key
              </Button>
              <Chip
                label={`${totalRequested} / ${deposit?.amount || 0} ${deposit?.metadata?.token || 'ETH'}`}
                sx={{
                  fontWeight: 700, fontSize: '0.85rem', fontFamily: '"JetBrains Mono", monospace',
                  bgcolor: exceedsDeposit ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                  color: exceedsDeposit ? '#ef4444' : '#22c55e',
                }}
              />
            </Box>
            {exceedsDeposit && (
              <Alert severity="error" sx={{ mb: 2 }}>Total exceeds deposit amount</Alert>
            )}

            {/* Execute button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={!canExecute || isProcessing}
              onClick={handleExecute}
              sx={{
                py: 1.5,
                background: canExecute ? 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)' : undefined,
                '&:hover': canExecute ? { background: 'linear-gradient(90deg, #16a34a 0%, #22c55e 100%)' } : {},
              }}
            >
              Unlock {validRequests.length} Key{validRequests.length !== 1 ? 's' : ''} via Relay
            </Button>

            {!dkgV2Ready && (
              <Alert severity="warning" sx={{ mt: 2 }}>DKG nodes not ready (need 3+ of 5)</Alert>
            )}
          </>
        )}

        {/* Signing phase */}
        {phase === 'signing' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ color: '#22c55e', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Sign Message</Typography>
            <Typography variant="body2" sx={{ color: subtleText }}>
              Please sign the proof message in your wallet to authenticate the key request.
            </Typography>
          </Box>
        )}

        {/* Requesting keys phase */}
        {phase === 'requesting' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={40} sx={{ color: '#22c55e', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Requesting Keys</Typography>
            <Typography variant="body2" sx={{ color: subtleText }}>
              {progress?.message || 'Collecting keyshares from DKG nodes...'}
            </Typography>
            <LinearProgress sx={{ mt: 2, '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #22c55e, #16a34a)' } }} />
          </Box>
        )}

        {/* Withdrawing phase */}
        {phase === 'withdrawing' && (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
              Unlocking via Relay
            </Typography>
            {withdrawStatuses.map((ws, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, p: 1.5, borderRadius: 1, border: '1px solid', borderColor,
                bgcolor: ws.status === 'success' ? (isDark ? 'rgba(34,197,94,0.05)' : 'rgba(34,197,94,0.08)')
                  : ws.status === 'failed' ? (isDark ? 'rgba(239,68,68,0.05)' : 'rgba(239,68,68,0.08)')
                  : 'transparent',
              }}>
                {ws.status === 'withdrawing' && <CircularProgress size={18} sx={{ color: '#eab308' }} />}
                {ws.status === 'success' && <CheckCircle sx={{ color: '#22c55e', fontSize: 20 }} />}
                {ws.status === 'failed' && <Typography sx={{ color: '#ef4444', fontSize: 20 }}>✗</Typography>}
                {ws.status === 'pending' && <Typography sx={{ color: subtleText, fontSize: 14 }}>○</Typography>}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {requests[i]?.denomination} {requests[i]?.token}
                  </Typography>
                  <Typography variant="caption" sx={{ color: subtleText }}>
                    → {requests[i]?.recipientAddress?.slice(0, 8)}...{requests[i]?.recipientAddress?.slice(-4)}
                  </Typography>
                  {ws.error && <Typography variant="caption" sx={{ color: '#ef4444', display: 'block' }}>{ws.error}</Typography>}
                  {ws.txHash && (
                    <Button size="small" endIcon={<Launch sx={{ fontSize: 12 }} />}
                      href={getExplorerUrl(ws.txHash, requests[i]?.chain)} target="_blank" rel="noopener noreferrer"
                      sx={{ fontSize: '0.65rem', p: 0, minWidth: 'auto', textTransform: 'none', color: '#22c55e' }}>
                      View tx
                    </Button>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Done phase */}
        {phase === 'done' && (
          <Box sx={{ py: 2 }}>
            {withdrawStatuses.every(s => s.status === 'success') ? (
              <Alert severity="success" sx={{ mb: 2 }}>All withdrawals completed successfully!</Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {withdrawStatuses.filter(s => s.status === 'failed').length} withdrawal(s) failed. You can retry from the Dashboard.
              </Alert>
            )}
            {withdrawStatuses.map((ws, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, p: 1.5, borderRadius: 1, border: '1px solid', borderColor }}>
                {ws.status === 'success' ? <CheckCircle sx={{ color: '#22c55e', fontSize: 20 }} /> : <Typography sx={{ color: '#ef4444', fontSize: 20 }}>✗</Typography>}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {requests[i]?.denomination} {requests[i]?.token} on {supportedChains.find(c => c.chain_name === requests[i]?.chain)?.name || requests[i]?.chain}
                  </Typography>
                  {ws.txHash && (
                    <Button size="small" endIcon={<Launch sx={{ fontSize: 12 }} />}
                      href={getExplorerUrl(ws.txHash, requests[i]?.chain)} target="_blank" rel="noopener noreferrer"
                      sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto', textTransform: 'none', color: '#22c55e' }}>
                      View transaction
                    </Button>
                  )}
                  {ws.error && <Typography variant="caption" sx={{ color: '#ef4444', display: 'block' }}>{ws.error}</Typography>}
                </Box>
              </Box>
            ))}
            <Button fullWidth variant="outlined" onClick={onClose} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WithdrawNowDialog;
